import { useEffect } from "react";

const SITE_URL = "https://spinlabs.in";
const DEFAULT_OG = "https://spinlabs.in/og-cover.png";
const SITE_NAME = "Spin Labs";

const upsertMeta = (key, attr, value) => {
  if (value == null) return null;
  let el = document.head.querySelector(`meta[${key}="${attr}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, attr);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return el;
};

const upsertLink = (rel, href) => {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  type = "website",
  noindex = false,
  jsonLd = null,
  publishedTime,
  modifiedTime,
  author,
  keywords,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Software, AI, Mobile & SaaS Studio`;
    const canonical = `${SITE_URL}${path}`;
    document.title = fullTitle;

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:site_name", SITE_NAME);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    if (type === "article") {
      upsertMeta("property", "article:published_time", publishedTime);
      upsertMeta("property", "article:modified_time", modifiedTime || publishedTime);
      upsertMeta("property", "article:author", author);
    }

    let jsonLdEl = null;
    if (jsonLd) {
      const payload = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.dataset.seo = "page";
      jsonLdEl.textContent = JSON.stringify(payload.length === 1 ? payload[0] : payload);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      if (jsonLdEl && jsonLdEl.parentNode) jsonLdEl.parentNode.removeChild(jsonLdEl);
    };
  }, [title, description, path, image, type, noindex, jsonLd, publishedTime, modifiedTime, author, keywords]);

  return null;
}

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});
